import type {
  BusinessExpenseInsights,
  BusinessExpenseSummaryKpis,
  ExpenseByCategoryRow,
  ReportDateRange,
  ReportTransactionRow,
} from '../../types/report.types';
import type { ReportingUserContext } from '../../types/reporting-user-context';
import { buildBusinessExpenseGstSummaryFromRows } from './business-expense-gst-summary';
import { buildBusinessExpenseQuarterTotals } from './business-expense-quarters';
import { buildBusinessExpenseCategoryYearOnYear } from './business-expense-yoy';
import { buildBusinessExpenseVendorSummary } from './business-expense-vendor';
import {
  businessExpenseDataNotices,
  detectGstAndReceiptGaps,
  evaluateBusinessExpenseRules,
} from './business-expense-rules';

const AI_VENDOR_LABELS = new Set([
  'OpenAI / ChatGPT',
  'Anthropic / Claude',
  'Cursor',
  'Replit',
  'Perplexity',
]);

function buildSummaryKpis(transactions: ReportTransactionRow[]): BusinessExpenseSummaryKpis {
  const totalSpendAud = transactions.reduce(
    (s, t) => s + (t.expenseAmountAud ?? Math.abs(t.amount)),
    0
  );
  const claimableRows = transactions.filter((t) => t.claimableAmountAud != null);
  const claimableAmountAud = claimableRows.length
    ? claimableRows.reduce((s, t) => s + (t.claimableAmountAud ?? 0), 0)
    : null;
  const apportionedSpend = claimableRows.reduce(
    (s, t) => s + (t.expenseAmountAud ?? Math.abs(t.amount)),
    0
  );
  const unapportionedSpendAud = totalSpendAud - apportionedSpend;
  const gst = buildBusinessExpenseGstSummaryFromRows(transactions);

  return {
    totalSpendAud,
    claimableAmountAud,
    unapportionedSpendAud,
    transactionCount: transactions.length,
    gstIncludedConfirmedAud: gst?.gstIncludedConfirmed ?? 0,
  };
}

function estimateLatestMonthAiSpend(transactions: ReportTransactionRow[]): number | undefined {
  if (!transactions.length) return undefined;
  const latestMonth = transactions[transactions.length - 1].date.slice(0, 7);
  return transactions
    .filter(
      (t) =>
        t.date.slice(0, 7) === latestMonth &&
        t.vendorLabel &&
        AI_VENDOR_LABELS.has(t.vendorLabel)
    )
    .reduce((s, t) => s + (t.expenseAmountAud ?? Math.abs(t.amount)), 0);
}

export function buildBusinessExpenseInsights(input: {
  period: ReportDateRange;
  transactions: ReportTransactionRow[];
  expenseByCategory: ExpenseByCategoryRow[];
  priorExpenseByCategory: ExpenseByCategoryRow[];
  priorPeriodHasData: boolean;
  userContext?: ReportingUserContext;
}): BusinessExpenseInsights {
  const gstRegistered = Boolean(input.userContext?.gstRegistered);
  const ruleFindings = evaluateBusinessExpenseRules({
    transactions: input.transactions,
    periodEndDate: input.period.endDate,
    gstRegistered,
    currentAiVendorMonthlySpendAud: estimateLatestMonthAiSpend(input.transactions),
  });
  const txNotices = detectGstAndReceiptGaps(input.transactions);

  return {
    summaryKpis: buildSummaryKpis(input.transactions),
    dataNotices: businessExpenseDataNotices(ruleFindings, txNotices),
    informationalFindings: ruleFindings.filter(
      (f) => f.id.startsWith('duplicateCharge') || f.severity === 'low'
    ),
    vendors: buildBusinessExpenseVendorSummary(input.transactions),
    quarterlyTotals: buildBusinessExpenseQuarterTotals(input.period, input.transactions),
    categoryYearOnYear: buildBusinessExpenseCategoryYearOnYear(
      input.expenseByCategory,
      input.priorExpenseByCategory,
      input.priorPeriodHasData
    ),
    showGstSection: gstRegistered,
    businessName: input.userContext?.businessName ?? null,
    businessAbn: input.userContext?.businessAbn ?? null,
  };
}
